// src/components/scan/ScanResults/ScanTableEmails.tsx
import { ArrowRight, Lock, MailWarning } from 'lucide-react';
import React from 'react';
import { useIsMobile } from '../../../utils/isMobile';

interface ScanTableEmailsProps {
  t: (key: string) => string;
  reset: boolean;
  maskedEmails: string[];
  emailsCount: number;
  onViewMore: () => void;
}

const ScanTableEmails: React.FC<ScanTableEmailsProps> = ({
  t,
  maskedEmails,
  emailsCount,
  onViewMore,
}) => {
  const isMobile = useIsMobile();
  const MAX_VISIBLE_EMAILS = 5;
  const remaining =
    emailsCount > MAX_VISIBLE_EMAILS
      ? emailsCount - MAX_VISIBLE_EMAILS
      : 0;

  // Responsive classes
  const containerPadding = isMobile ? 'p-4' : 'p-6';
  const titleSize        = isMobile ? 'text-lg' : 'text-xl';
  const iconSize         = isMobile ? 'w-12 h-12' : 'w-16 h-16';
  const cellPadding      = isMobile ? 'p-2' : 'p-4';
  const headerTextSize   = isMobile ? 'text-sm' : 'text-lg';
  const rowTextSize      = isMobile ? 'text-sm' : 'text-base';
  const lockIconSize     = isMobile ? 'w-3 h-3' : 'w-4 h-4';
  const btnPadding       = isMobile ? 'px-4 py-2' : 'px-6 py-2';
  const btnTextSize      = isMobile ? 'text-base' : 'text-lg';
  const arrowSize        = isMobile ? 'w-4 h-4' : 'w-5 h-5';
  const remainingSize    = isMobile ? 'text-base' : 'text-lg';

  const viewMoreButtonClasses = `
    bg-gradient-to-r from-yellow-500 to-yellow-700
    hover:from-yellow-600 hover:to-yellow-800
    rounded-lg flex items-center gap-2
    transition-all text-white shadow-md shadow-yellow-900
    ${btnPadding} ${btnTextSize}
  `;

  return (
    <div
      className={`
        mt-8 bg-gray-900 bg-opacity-60 backdrop-blur-md
        rounded-xl shadow-lg shadow-black/30 border border-gray-700
        ${containerPadding}
      `}
    >
      <h3
        className={`
          font-semibold text-gray-200 mb-6 text-center
          ${titleSize}
        `}
      >
        {t('scan.foundEmails')}
      </h3>

      {maskedEmails.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <MailWarning
            className={`${iconSize} text-green-400 mb-4`}
          />
          <p className={`text-gray-300 font-medium ${rowTextSize}`}>
            {t('scan.noEmailsFound')}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg">
            <table className="table-fixed w-full text-left text-white border-collapse">
              <thead>
                <tr className="bg-gray-800 text-gray-300">
                  <th className={`${cellPadding} ${headerTextSize} w-2/3`}>
                    {t('scan.email')}
                  </th>
                  <th className={`${cellPadding} ${headerTextSize} w-1/3 text-center`}>
                    {t('scan.acquisitionMethod')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {maskedEmails.slice(0, MAX_VISIBLE_EMAILS).map((email, i) => (
                  <tr
                    key={i}
                    className="border-t border-gray-600 hover:bg-gray-700 transition"
                  >
                    <td
                      className={`
                        ${cellPadding} break-words
                        text-gray-300 ${rowTextSize}
                      `}
                    >
                      {email}
                    </td>
                    <td
                      className={`
                        ${cellPadding} text-center text-gray-400
                        flex items-center justify-center gap-2
                        ${rowTextSize}
                      `}
                    >
                      <Lock className={lockIconSize} />
                      <span>{t('scan.locked')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {emailsCount > MAX_VISIBLE_EMAILS && (
            <div className="mt-6 flex flex-col items-center">
              {remaining > 0 && (
                <span
                  className={`
                    text-gray-400 font-semibold mb-4
                    ${remainingSize}
                  `}
                >
                  {remaining} {t('scan.more')}
                </span>
              )}
              <button
                className={viewMoreButtonClasses}
                onClick={onViewMore}
              >
                {t('scan.viewMore')}
                <ArrowRight className={arrowSize} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScanTableEmails;
